import React, { Suspense, useState, useEffect, useCallback, memo } from "react";
import {
  Github,
  Linkedin,
  Mail,
  ExternalLink,
  Instagram,
  Sparkles,
} from "lucide-react";
import { Canvas } from "@react-three/fiber";
import { ContactShadows, OrbitControls } from "@react-three/drei";
import { HeroRobot } from "../Components/RobotCompanion";
import AOS from "aos";
import "aos/dist/aos.css";

// Memoized Components
const StatusBadge = memo(() => (
  <div
    className="inline-block animate-float lg:mx-0"
    data-aos="zoom-in"
    data-aos-delay="400"
  >
    <div className="relative group">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-[#6366f1] to-[#a855f7] rounded-full blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
      <div className="relative px-3 py-2 border rounded-full sm:px-4 bg-black/40 backdrop-blur-xl border-white/10">
        <span className="bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-transparent bg-clip-text sm:text-sm text-[0.7rem] font-medium flex items-center">
          <Sparkles className="w-3 h-3 mr-2 text-blue-400 sm:w-4 sm:h-4" aria-hidden="true" />
          Building Intelligent Systems
        </span>
      </div>
    </div>
  </div>
));
StatusBadge.displayName = 'StatusBadge';

/**
 * H1 — only one per page. Keyword-rich: "Full Stack + Gen AI Developer"
 * Supplemented by the name "Avansh Yadav" in the About section below the fold.
 */
const MainTitle = memo(() => (
  <div className="space-y-2" data-aos="fade-up" data-aos-delay="600">
    <h1 className="text-[clamp(2.35rem,13vw,3.75rem)] font-bold tracking-tight leading-[1.05] sm:text-6xl lg:text-6xl xl:text-7xl">
      <span className="relative inline-block">
        <span className="absolute -inset-2 bg-gradient-to-r from-[#6366f1] to-[#a855f7] blur-2xl opacity-20" aria-hidden="true"></span>
        <span className="relative text-transparent bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text">
          Full Stack +
        </span>
      </span>
      <br />
      <span className="relative inline-block mt-2">
        <span className="absolute -inset-2 bg-gradient-to-r from-[#6366f1] to-[#a855f7] blur-2xl opacity-20" aria-hidden="true"></span>
        <span className="relative bg-gradient-to-r from-[#6366f1] via-[#8b5cf6] to-[#a855f7] bg-clip-text text-transparent">
          Gen AI Developer
        </span>
      </span>
    </h1>
  </div>
));
MainTitle.displayName = 'MainTitle';

const TechStack = memo(({ tech }) => (
  <div className="hidden px-4 py-2 text-sm text-gray-300 transition-colors border rounded-full sm:block bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10">
    {tech}
  </div>
));
TechStack.displayName = 'TechStack';

const CTAButton = memo(({ href, text, icon: Icon }) => (
  <a href={href} className="w-full min-[420px]:w-auto" aria-label={`Navigate to ${text} section`}>
    <button className="group relative w-full min-[420px]:w-[160px]" type="button">
      <div className="absolute -inset-0.5 bg-gradient-to-r from-[#4f52c9] to-[#8644c5] rounded-xl opacity-50 blur-md group-hover:opacity-90 transition-all duration-700" aria-hidden="true"></div>
      <div className="relative h-11 bg-[#030014] backdrop-blur-xl rounded-lg border border-white/10 leading-none overflow-hidden">
        <div className="absolute inset-0 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 bg-gradient-to-r from-[#4f52c9]/20 to-[#8644c5]/20" aria-hidden="true"></div>
        <span className="absolute inset-0 flex items-center justify-center gap-2 text-sm transition-all duration-300 group-hover:gap-3">
          <span className="z-10 font-medium text-transparent bg-gradient-to-r from-gray-200 to-white bg-clip-text">
            {text}
          </span>
          <Icon
            className={`w-4 h-4 text-gray-200 ${
              text === "Contact"
                ? "group-hover:translate-x-1"
                : "group-hover:rotate-45"
            } transform transition-all duration-300 z-10`}
            aria-hidden="true"
          />
        </span>
      </div>
    </button>
  </a>
));
CTAButton.displayName = 'CTAButton';

const SocialLink = memo(({ icon: Icon, link, label }) => (
  <a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`Visit Avansh Yadav on ${label}`}
  >
    <button className="relative p-3 group" type="button" aria-label={label}>
      <div className="absolute inset-0 bg-gradient-to-r from-[#6366f1] to-[#a855f7] rounded-xl blur opacity-20 group-hover:opacity-40 transition duration-300" aria-hidden="true"></div>
      <div className="relative flex items-center justify-center p-2 transition-all duration-300 border rounded-xl bg-black/50 backdrop-blur-xl border-white/10 group-hover:border-white/20">
        <Icon className="w-5 h-5 text-gray-400 transition-colors group-hover:text-white" aria-hidden="true" />
      </div>
    </button>
  </a>
));
SocialLink.displayName = 'SocialLink';

const RobotLoader = () => (
  <div className="flex flex-col items-center justify-center w-full h-full gap-3" role="status" aria-label="Loading 3D Robot">
    <div className="w-14 h-14 rounded-full border-2 border-indigo-500/30 border-t-cyan-400 animate-spin" />
    <p className="text-xs text-gray-500">Loading 3D Robot...</p>
  </div>
);

// Constants
const TYPING_SPEED = 100;
const ERASING_SPEED = 50;
const PAUSE_DURATION = 2000;

/**
 * These typing words are NOT the H1 — they are animated subtitles.
 * The H1 "Full Stack + Gen AI Developer" is static and crawlable.
 */
const WORDS = [
  "Full Stack Developer",
  "AI/ML Engineer",
  "Cloud Architect",
  "GenAI Specialist",
  "DevOps Enthusiast",
];

const TECH_STACK = [
  "Python",
  "React",
  "Node.js",
  "TensorFlow",
  "LangChain",
  "Docker",
  "AWS",
  "MongoDB",
  "PostgreSQL",
  "FastAPI",
];

const SOCIAL_LINKS = [
  { icon: Github, link: "https://github.com/Avansh2006", label: "GitHub" },
  { icon: Linkedin, link: "https://www.linkedin.com/in/avanshyadav/", label: "LinkedIn" },
  { icon: Instagram, link: "https://www.instagram.com/yadavav2006", label: "Instagram" },
];

const Home = () => {
  const [text, setText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Optimize AOS initialization
  useEffect(() => {
    const initAOS = () => {
      AOS.init({
        once: true,
        offset: 10,
      });
    };

    initAOS();
    window.addEventListener("resize", initAOS);
    return () => window.removeEventListener("resize", initAOS);
  }, []);

  useEffect(() => {
    setIsLoaded(true);
    return () => setIsLoaded(false);
  }, []);

  // Optimize typing effect
  const handleTyping = useCallback(() => {
    if (isTyping) {
      if (charIndex < WORDS[wordIndex].length) {
        setText((prev) => prev + WORDS[wordIndex][charIndex]);
        setCharIndex((prev) => prev + 1);
      } else {
        setTimeout(() => setIsTyping(false), PAUSE_DURATION);
      }
    } else {
      if (charIndex > 0) {
        setText((prev) => prev.slice(0, -1));
        setCharIndex((prev) => prev - 1);
      } else {
        setWordIndex((prev) => (prev + 1) % WORDS.length);
        setIsTyping(true);
      }
    }
  }, [charIndex, isTyping, wordIndex]);

  useEffect(() => {
    const timeout = setTimeout(
      handleTyping,
      isTyping ? TYPING_SPEED : ERASING_SPEED
    );
    return () => clearTimeout(timeout);
  }, [handleTyping]);

  return (
    <div
      className="min-h-screen bg-[#030014] dark:bg-[#030014] light:bg-gradient-to-br light:from-blue-50 light:via-indigo-50 light:to-purple-50 overflow-x-hidden transition-colors duration-300"
      id="Home"
    >
      <div
        className={`relative z-10 transition-all duration-1000 ${
          isLoaded ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="container mx-auto px-[5%] sm:px-6 lg:px-[5%] xl:px-0 min-h-screen">
          <div className="flex flex-col items-center justify-center min-h-screen h-auto gap-8 pt-24 pb-12 lg:flex-row md:justify-between sm:gap-12 lg:gap-20 lg:pt-16 lg:pb-0">
            {/* Left Column */}
            <div
              className="order-1 w-full max-w-2xl space-y-6 text-left lg:w-1/2 sm:space-y-8 lg:text-left lg:order-1 lg:mt-0"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="space-y-4 sm:space-y-6">
                <StatusBadge />
                <MainTitle />

                {/* Animated typing subtitle — decorative, not the H1 */}
                <div
                  className="flex items-center min-h-8"
                  data-aos="fade-up"
                  data-aos-delay="800"
                  aria-label={`Currently: ${text}`}
                >
                  <span className="text-xl font-light text-transparent md:text-2xl bg-gradient-to-r from-gray-100 to-gray-300 dark:from-gray-100 dark:to-gray-300 light:from-gray-700 light:to-gray-900 bg-clip-text">
                    {text}
                  </span>
                  <span className="w-[3px] h-6 bg-gradient-to-t from-[#6366f1] to-[#a855f7] ml-1 animate-blink" aria-hidden="true"></span>
                </div>

                {/* Professional Bio — keyword-rich description for SEO */}
                <p
                  className="max-w-xl text-base font-light leading-relaxed text-gray-400 dark:text-gray-400 light:text-gray-700 md:text-lg transition-colors duration-300"
                  data-aos="fade-up"
                  data-aos-delay="1000"
                >
                  Passionate web developer and competitive programmer building
                  innovative, efficient, and user-friendly digital solutions
                  using React, Node.js, and Generative AI.
                </p>

                {/* Tech Stack pills */}
                <div
                  className="flex flex-wrap justify-start gap-3"
                  data-aos="fade-up"
                  data-aos-delay="1200"
                  aria-label="Technology stack"
                >
                  {TECH_STACK.map((tech, index) => (
                    <TechStack key={index} tech={tech} />
                  ))}
                </div>

                {/* CTA Buttons */}
                <div
                  className="flex flex-col justify-start w-full gap-3 min-[420px]:flex-row"
                  data-aos="fade-up"
                  data-aos-delay="1400"
                >
                  <CTAButton
                    href="#About"
                    text="About"
                    icon={ExternalLink}
                  />
                  <CTAButton href="#Contact" text="Contact" icon={Mail} />
                </div>

                {/* Social Links */}
                <div
                  className="justify-start hidden gap-4 sm:flex"
                  data-aos="fade-up"
                  data-aos-delay="1600"
                >
                  {SOCIAL_LINKS.map((social, index) => (
                    <SocialLink key={index} {...social} />
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Interactive 3D Animation */}
            <div
              className="w-full max-w-[520px] py-0 lg:w-1/2 h-[300px] min-[420px]:h-[360px] sm:h-[430px] md:h-[500px] lg:h-[600px] xl:h-[750px] relative flex items-center justify-center order-2 lg:order-2 mt-2 lg:mt-0 overflow-hidden lg:overflow-visible"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              data-aos="fade-left"
              data-aos-delay="600"
              aria-label="3D interactive robot animation"
              role="img"
            >
              <div className="relative w-full h-full opacity-90">
                <div
                  className={`absolute inset-0 bg-gradient-to-r from-[#6366f1]/10 to-[#a855f7]/10 rounded-3xl blur-3xl transition-all duration-700 ease-in-out ${
                    isHovering ? "opacity-50 scale-105" : "opacity-20 scale-100"
                  }`}
                  aria-hidden="true"
                ></div>

                <div
                  className={`relative z-10 w-full h-full opacity-90 transform transition-transform duration-500 ${
                    isHovering ? "scale-105" : "scale-100"
                  }`}
                >
                  <Canvas
                    camera={{ position: [0, 0, 5], fov: 35 }}
                    gl={{ antialias: true, alpha: true }}
                    style={{ width: "100%", height: "100%", background: "transparent" }}
                    dpr={Math.min(window.devicePixelRatio, 2)}
                  >
                    <Suspense fallback={<RobotLoader />}>
                      <HeroRobot mode="ambient" disableScrollOffset />
                      <ContactShadows position={[0, -2.5, 0]} opacity={0.4} scale={6} blur={2} far={4} color="#4f46e5" />
                      <OrbitControls enableZoom={false} enablePan={false} enableRotate />
                    </Suspense>
                  </Canvas>
                </div>

                <div
                  className={`absolute inset-0 pointer-events-none transition-all duration-700 ${
                    isHovering ? "opacity-50" : "opacity-20"
                  }`}
                  aria-hidden="true"
                >
                  <div
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(400px,85vw)] h-[min(400px,85vw)] bg-gradient-to-br from-indigo-500/10 to-purple-500/10 blur-3xl animate-[pulse_6s_cubic-bezier(0.4,0,0.6,1)_infinite] transition-all duration-700 ${
                      isHovering ? "scale-110" : "scale-100"
                    }`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Home);
